// src/features/task/create/CustomFieldControl.tsx
// One custom field, rendered by its real `field_type`.
//
// All ten CustomFieldType values get a control that matches the data: a date
// field opens a date picker, a user field opens the user picker, a multi-select
// toggles chips. Placeholders, option colors and `validation` come from the
// definition, so an admin changing a field definition changes the form.

import { useI18n } from '@react/hooks';
import type { CustomFieldDefinition } from '@core/types/task';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { ChipMultiSelect } from '@/features/task/ChipMultiSelect';
import type { CustomFieldDraftValue } from './logic';

const CONTROL_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export interface CustomFieldControlProps {
  def: CustomFieldDefinition;
  /** DOM id — also anchors the label and the error message. */
  id: string;
  value: CustomFieldDraftValue | undefined;
  /** `label` carries a human-readable name for reference-type fields. */
  onChange: (value: CustomFieldDraftValue, label?: string) => void;
  onBlur?: () => void;
  error?: string;
  /** Scopes the user picker to a project or a team when creating inside one. */
  projectId?: string;
  teamId?: string;
}

export function CustomFieldControl({
  def,
  id,
  value,
  onChange,
  onBlur,
  error,
  projectId,
  teamId,
}: CustomFieldControlProps) {
  const { t } = useI18n();
  const text = Array.isArray(value) ? '' : (value ?? '');
  const list = Array.isArray(value) ? value : [];
  const describedBy = error ? `${id}-error` : undefined;
  const invalid = error ? true : undefined;

  const control = (() => {
    switch (def.field_type) {
      case 'CUSTOM_FIELD_TYPE_NUMBER':
        return (
          <input
            id={id}
            type="text"
            inputMode="decimal"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={def.placeholder || ''}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={`${CONTROL_CLASS} tabular-nums`}
          />
        );

      case 'CUSTOM_FIELD_TYPE_DATE':
        return (
          <input
            id={id}
            type="date"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={CONTROL_CLASS}
          />
        );

      case 'CUSTOM_FIELD_TYPE_DATETIME':
        return (
          <input
            id={id}
            type="datetime-local"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={CONTROL_CLASS}
          />
        );

      case 'CUSTOM_FIELD_TYPE_SELECT': {
        const options = [...(def.options || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
        const selected = options.find((o) => o.slug === text);
        return (
          <div className="flex items-center gap-2">
            {selected?.color && (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: selected.color }}
                aria-hidden="true"
              />
            )}
            <Select
              id={id}
              value={text}
              onChange={(e) => onChange(e.target.value)}
              onBlur={onBlur}
              error={error}
              aria-label={def.display_name}
              placeholder={def.placeholder || t('common.select')}
              options={[
                { value: '', label: def.placeholder || t('common.none') },
                ...options.map((o) => ({ value: o.slug, label: o.label })),
              ]}
            />
          </div>
        );
      }

      case 'CUSTOM_FIELD_TYPE_MULTI_SELECT': {
        const options = [...(def.options || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
        return (
          <div id={id} role="group" aria-label={def.display_name} aria-describedby={describedBy}>
            <ChipMultiSelect
              options={options.map((o) => ({ value: o.slug, label: o.label }))}
              selected={list}
              onToggle={(slug) =>
                onChange(list.includes(slug) ? list.filter((s) => s !== slug) : [...list, slug])
              }
              emptyLabel={t('task.createForm.noFieldOptions')}
            />
          </div>
        );
      }

      case 'CUSTOM_FIELD_TYPE_USER':
        return (
          <UserPicker
            value={text}
            onChange={(userId, displayName) => onChange(userId, displayName)}
            placeholder={def.placeholder || t('task.field.assigneePlaceholder')}
            error={error}
            projectId={projectId}
            teamId={teamId}
          />
        );

      case 'CUSTOM_FIELD_TYPE_ENTITY':
        return (
          <input
            id={id}
            type="text"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={def.placeholder || t('task.createForm.entityIdPlaceholder')}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={`${CONTROL_CLASS} font-mono`}
          />
        );

      case 'CUSTOM_FIELD_TYPE_URL':
        return (
          <input
            id={id}
            type="url"
            inputMode="url"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={def.placeholder || 'https://'}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={CONTROL_CLASS}
          />
        );

      case 'CUSTOM_FIELD_TYPE_CHECKBOX':
        return (
          <input
            id={id}
            type="checkbox"
            checked={text === 'true'}
            onChange={(e) => onChange(String(e.target.checked))}
            onBlur={onBlur}
            aria-describedby={describedBy}
            className="h-4 w-4 rounded border-input text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        );

      default:
        return (
          <input
            id={id}
            type="text"
            value={text}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            placeholder={def.placeholder || ''}
            maxLength={def.validation?.max_length || undefined}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            className={CONTROL_CLASS}
          />
        );
    }
  })();

  // Select and UserPicker render their own error text; the rest need one here.
  const ownsError =
    def.field_type === 'CUSTOM_FIELD_TYPE_SELECT' || def.field_type === 'CUSTOM_FIELD_TYPE_USER';

  return (
    <div className="min-w-0">
      {control}
      {error && !ownsError && (
        <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      )}
      {!error && def.description && (
        <p className="mt-1 text-xs text-muted-foreground">{def.description}</p>
      )}
    </div>
  );
}
